import * as MUI from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { WAREHOUSE_ITEMS } from "../../../../../../enums/QueryKeys";
import useUser from "../../../../../../hooks/useUser";
import logger from "../../../../../../utils/logger";
import useUpdateWarehouseItem from "../hooks/useUpdateWarehouseItem";
import { emptyValues, QUANTITY_STEPS } from "./WarehouseItemForm";

export default function QuantityForm({
  quantity = 0,
  item = null,
  handleQuantityClose,
}) {
  const { id } = useParams();
  const { roles, currentClinic } = useUser();
  const clinicId = roles?.master ? id : currentClinic?.id;
  const queryClient = useQueryClient();
  const { update, isUpdating } = useUpdateWarehouseItem(clinicId);
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...emptyValues,
      ...(item ?? {}),
      quantity: quantity,
      category: item?.category?.name ?? "",
      supplier: item?.supplier?.name ?? "",
      antiRabiesVaccine: Boolean(item?.antiRabiesVaccine),
      otherVaccine: Boolean(item?.otherVaccine),
      narcoticDrug: Boolean(item?.narcoticDrug),
      prescriptionRequired: Boolean(item?.prescriptionRequired),
      expirationDate: item?.expirationDate ? dayjs(item.expirationDate) : null,
    },
  });

  const submitData = async (data) => {
    if (item.id == null) return;
    const payload = {
      title: data.title.trim(),
      description: data?.description?.trim() ?? "",
      categoryName: data?.category?.trim(),
      supplierName: data.supplier?.trim(),
      price: Number(data.price),
      pdv: Number(data.pdv),
      quantity: Number(data.quantity),
      code: data.code?.trim(),
      serialNumber: data.serialNumber?.trim(),
      measuringUnit: data.measuringUnit?.trim(),
      discount: Number(data.discount),
      antiRabiesVaccine: Boolean(data.antiRabiesVaccine),
      otherVaccine: Boolean(data.otherVaccine),
      narcoticDrug: Boolean(data.narcoticDrug),
      prescriptionRequired: Boolean(data.prescriptionRequired),
      expirationDate: data.expirationDate
        ? dayjs(data.expirationDate).format("YYYY-MM-DD")
        : null,
    };

    try {
      await update(item.id, payload);
      queryClient.invalidateQueries({ queryKey: [WAREHOUSE_ITEMS] });
    } catch (e) {
      logger.error("Error trying to update quantity: " + e);
    }
  };
  return (
    <form onSubmit={handleSubmit(submitData)}>
      <Controller
        control={control}
        name="quantity"
        rules={{
          min: { value: 0, message: t("MustBePositiveNumber") },
        }}
        render={({ field }) => {
          const currentValue = Number(field.value) || 0;
          const adjust = (delta) => {
            field.onChange(Math.max(0, currentValue + delta));
          };

          return (
            <MUI.Stack spacing={1.25}>
              <MUI.Typography variant="caption" color="text.secondary">
                {t("Quantity")}
              </MUI.Typography>
              <MUI.Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                flexWrap="wrap"
                useFlexGap
              >
                {QUANTITY_STEPS.filter((step) => step < 0).map((step) => (
                  <MUI.Button
                    key={step}
                    variant="outlined"
                    size="medium"
                    onClick={() => adjust(step)}
                    disabled={currentValue <= 0}
                    sx={{ borderRadius: 2, minWidth: 56 }}
                  >
                    {step}
                  </MUI.Button>
                ))}
                <MUI.TextField
                  {...field}
                  type="number"
                  size="small"
                  sx={{ width: 140 }}
                  inputProps={{ min: 0, step: "1" }}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                />
                {QUANTITY_STEPS.filter((step) => step > 0).map((step) => (
                  <MUI.Button
                    key={step}
                    variant="outlined"
                    size="medium"
                    onClick={() => adjust(step)}
                    sx={{ borderRadius: 2, minWidth: 56 }}
                  >
                    +{step}
                  </MUI.Button>
                ))}
              </MUI.Stack>
            </MUI.Stack>
          );
        }}
      />
      <MUI.Stack direction="row" spacing={1} justifyContent="flex-end" mt={5}>
        <MUI.Button size="small" variant="contained" type="submit">
          {t("Save")}
        </MUI.Button>
        <MUI.Button size="small" onClick={handleQuantityClose} color="error">
          {t("Close")}
        </MUI.Button>
      </MUI.Stack>
    </form>
  );
}
